import { useEffect } from "react";
import { updateFunnelSessionSummary } from "../../services/funnel/funnelEventStore";
import {
  buildQrFlyerReferralAttribution,
  isValidReferralCode,
  normalizeFlyerInstanceId,
  normalizeReferralCode,
  PENDING_REFERRAL_CODE_STORAGE_KEY,
  savePendingReferralAttribution,
  savePendingReferralCode,
  QR_FLYER_REFERRAL_CHANNEL,
} from "../../services/referralStorage";
import { isPersistentLocalStorageAvailable } from "../../utils/safeStorage";

const isQrFlyerUrl = (url) => {
  const version = String(url.searchParams.get("v") || "").trim();
  const referralCode = normalizeReferralCode(url.searchParams.get("r") || "");
  const flyerInstanceId = normalizeFlyerInstanceId(url.searchParams.get("f") || "");

  return (
    Boolean(referralCode) &&
    Boolean(flyerInstanceId) &&
    /^[a-z0-9]{1,40}$/i.test(version)
  );
};

export function useCaptureReferralFromUrl({
  sessionId,
  paramName = "referral",
  channel = "url",
  storageKey = PENDING_REFERRAL_CODE_STORAGE_KEY,
  recordSessionSummary = true,
} = {}) {
  useEffect(() => {
    const url = new URL(window.location.href);
    const qrFlyerVisit = isQrFlyerUrl(url);
    const raw = qrFlyerVisit
      ? url.searchParams.get("r") || ""
      : url.searchParams.get(paramName) || "";
    const code = normalizeReferralCode(raw);

    if (!code || !isValidReferralCode(code)) return;

    const qrAttribution = qrFlyerVisit
      ? buildQrFlyerReferralAttribution({
          referralCode: code,
          flyerVersion: url.searchParams.get("v") || "",
          flyerInstanceId: url.searchParams.get("f") || "",
        })
      : null;

    try {
      savePendingReferralCode(code, storageKey);
      if (qrAttribution) {
        savePendingReferralAttribution(qrAttribution);
      }
    } catch (e) {
      // ignore storage failures
    }

    if (isPersistentLocalStorageAvailable()) {
      url.searchParams.delete(paramName);
      url.searchParams.delete("r");
      if (qrFlyerVisit) {
        url.searchParams.delete("q");
        url.searchParams.delete("v");
        url.searchParams.delete("h");
        url.searchParams.delete("c");
        url.searchParams.delete("f");
      }
      const newUrl =
        url.pathname +
        (url.searchParams.toString() ? `?${url.searchParams.toString()}` : "") +
        url.hash;
      window.history.replaceState({}, "", newUrl);
    }

    (async () => {
      try {
        if (recordSessionSummary && sessionId) {
          await updateFunnelSessionSummary({
            sessionId,
            attribution: qrAttribution || {
              source: "referral",
              sourceFamily: "other",
            },
            properties: {
              entrySource: qrFlyerVisit
                ? `referral_${QR_FLYER_REFERRAL_CHANNEL}`
                : `referral_${channel || "url"}`,
              source: qrFlyerVisit ? "qr" : "referral",
              channel: qrFlyerVisit
                ? QR_FLYER_REFERRAL_CHANNEL
                : channel || "url",
              hasReferralCode: true,
              referralCode: code,
              flyerVersion: qrAttribution?.flyerVersion,
              flyerInstanceId: qrAttribution?.flyerInstanceId,
            },
          });
        }
      } catch (e) {
        console.error("Failed to record referral session summary:", e);
      }
    })();
  }, [sessionId, paramName, channel, storageKey, recordSessionSummary]);
}
